import React, { useState, useRef, useEffect } from 'react';
import styled from 'styled-components';
import { motion, AnimatePresence } from 'framer-motion';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faHome } from '@fortawesome/free-solid-svg-icons';
import VideoPlayer from './components/VideoPlayer';
import RhythmGame from './components/RhythmGame';
import Countdown from './components/Countdown';
import Ranking from './components/Ranking';
import EntryForm from './components/EntryForm';
import GameResultForm from './components/GameResultForm';
import RoundResult from './components/RoundResult';
import RouletteEvent from './components/RouletteEvent';
import './App.css';
import { BEATMAPS } from './config/beatmaps';
import { 
  GAME_STATES, 
  GAME_SETTINGS, 
  getGameTypeFromState, 
  getGameSettings,
  INITIAL_STATE,
  GAME_CONFIG
} from './config/gameConfig';
import { RHYTHM_CONFIG } from './config/RhythmConfig';
import AudioController from './utils/audioController';
import { toKstIso, getRhythmMedia, getMainVideoId } from './utils/firestoreApi';
import { Timestamp, getFirestore, doc, setDoc, getDoc, serverTimestamp } from 'firebase/firestore';
import VideoPlayerIframe from './components/VideoPlayerIframe';
import ComingSoon from './components/ComingSoon';
import PuzzleGame from './components/PuzzleGame';
import WorldCup from './components/WorldCup';
import QuizGame from './components/QuizGame';
import DripBattle from './components/DripBattle';
import Login from './components/Login';
import { auth } from './config/firebaseConfig';
import { onAuthStateChanged, signOut } from 'firebase/auth';
// getUserInfo import 제거 - currentUser만 사용
import { hasUserWonRoulette } from './utils/firestoreApi';
import MyPage from './components/mypage';
import DinoGame from './components/DinoGame';
import InfoPopup from './components/InfoPopup';
import RouletteBanner from './components/RouletteBanner.js';
import StressTest from './data/StressTest';

const AppLayout = styled.div`
  position: relative;
  width: 100vw;
  min-height: 100vh;
  overflow-y: auto;
  overflow-x: hidden;
  display: flex;
  flex-direction: column;
  align-items: center;
  box-sizing: border-box;

  /* 1) 검정 반투명 오버레이 (맨 위)
     2) BG 이미지 (중간)
     3) 보라색 그라데이션 (맨 아래) */
  background:
    /* overlay as a gradient image */
    linear-gradient(rgba(0,0,0,0.3), rgba(0,0,0,0.3)),
    /* actual BG image, vertical fit */
    url('/BG.jpg') center top / auto 100vh no-repeat fixed,
    /* purple gradient fill */
    linear-gradient(150deg,rgb(0, 0, 0) 0%,rgb(0, 0, 0) 100%);
  
  /* 오버레이만 multiply, 나머지는 normal */
  background-blend-mode: multiply, normal, normal;
`;

const BackgroundImage = styled.div`
  position: absolute;
  inset: 0;                     /* top/right/bottom/left 모두 0 */
  z-index: -1;                  /* 뒤로 보내기 */

  /* 이미지+오버레이 */
  background-image: url('/BG.jpeg');
  background-color: rgba(0, 0, 0, 0.3);
  background-blend-mode: multiply; 

  /* 화면 & 스크롤 영역 전체를 꽉 채우기 */
  background-size: cover;
  background-position: center center;
  background-repeat: no-repeat;
  background-attachment: fixed;
`;

const Banner = styled.div`
  width: 90%;
  max-width: 400px;
  height: 80px;
  margin: 0 auto;
  margin-top: calc(var(--vh, 1vh) * 2);
  margin-bottom: calc(var(--vh, 1vh) * 2);
  display: flex;
  justify-content: center;
  align-items: center;
  background: transparent;
  border-radius: 0;
  box-shadow: none;
  position: relative;

  @media (max-width: 768px) {
    width: 80%;
    max-width: 300px;
    height: 60px;
    margin-top: calc(var(--vh, 1vh) * 1);
    margin-bottom: calc(var(--vh, 1vh) * 1);
  }

  img {
    width: 100%;
    height: 100%;
    object-fit: contain;
    border-radius: 0;
    box-shadow: none;
  }
`;

const BackButton = styled.button`
  position: absolute;
  left: -30%;
  top: 50%;
  transform: translateY(-50%);
  width: 45px;
  height: 45px;
  border: none;
  border-radius: 50%;
  background: var(--primary-gradient);
  color: white;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  backdrop-filter: blur(10px);
  transition: all 0.3s ease;
  z-index: 1001;
  box-shadow: 0 4px 15px rgba(255, 107, 107, 0.4);

  &:hover {
    background: var(--primary-gradient);
    transform: translateY(-50%) scale(1.1);
    box-shadow: 0 6px 20px rgba(255, 107, 107, 0.6);
  }

  &:active {
    transform: translateY(-50%) scale(0.95);
  }

  @media (max-width: 768px) {
    left: -20px;
    width: 40px;
    height: 40px;
  }
`;

const MainContent = styled.div`
  flex: 1 1 0;
  width: 100%;
  min-height: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: flex-start;
  /* padding: 16px 0 24px 0; */
  max-width: 800px;
  margin: 0 auto;
  margin-top: 10px;
  position: relative;
  height: calc(var(--vh, 1vh) * 100 - 100px);

  @media (max-width: 768px) {
    max-width: 100vw;
    margin-top: 10px;
  }
`;

const HomeScreen = styled(motion.div)`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 100%;
  gap: 30px;
  max-width: 100%;

  @media (max-width: 768px) {
    gap: 20px;
  }
`;

const VideoContainer = styled.div`
  width: 100%;
  height: auto;
  padding: 35px;
  border-radius: 15px;
  box-shadow: 0 8px 25px rgba(0, 0, 0, 0.3);
  backdrop-filter: blur(10px);
  border: 1px solid rgba(255, 255, 255, 0.2);
  position: relative;
  min-height: 300px;
  display: flex;
  align-items: center;
  justify-content: center;

  @media (max-width: 768px) {
    max-width: 90%;
    padding: 20px;
    min-height: 200px;
  }
`;

const VideoLoadingSpinner = styled.div`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  width: 40px;
  height: 40px;
  border: 3px solid rgba(255, 255, 255, 0.3);
  border-top: 3px solid #ff6b6b;
  border-radius: 50%;
  animation: spin 1s linear infinite;
  z-index: 1;

  @keyframes spin {
    0% { transform: translate(-50%, -50%) rotate(0deg); }
    100% { transform: translate(-50%, -50%) rotate(360deg); }
  }
`;

const ButtonGridContainer = styled.div`
  background: rgba(255, 255, 255, 0.1);
  border-radius: 15px;
  padding: 10px 20px 20px 20px;
  margin-bottom: 20px;
  backdrop-filter: blur(10px);
  border: 1px solid rgba(255, 255, 255, 0.2);
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.1);
  width: 100%;
  max-width: 800px;

  @media (max-width: 768px) {
    max-width: 90%;
    padding: 15px;
  }
`;

const ButtonGrid = styled(motion.div)`
  display: flex;
  flex-direction: row;
  gap: 15px;
  width: 100%;
  justify-content: center;
  align-items: flex-end;

  @media (max-width: 768px) {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 12px;
  }
`;

const GameButton = styled(motion.button)`
  padding: 10px 15px;
  border: none;
  border-radius: 12px;
  font-size: 0.9rem;
  font-weight: bold;
  cursor: pointer;
  background: var(--primary-gradient);
  color: white;
  box-shadow: 0 4px 15px rgba(255, 107, 107, 0.4);
  min-height: 45px;
  display: flex;
  align-items: center;
  justify-content: center;

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
    transform: none;
    background: linear-gradient(45deg, #666666, #888888);
    box-shadow: 0 4px 15px rgba(102, 102, 102, 0.3);
  }
`;

const DisabledGameButton = styled(GameButton)`
  position: relative;
  margin-top: 20px;
  
  &::before {
    content: '8/14 앨범 발매 후';
    position: absolute;
    top: -20px;
    left: 50%;
    transform: translateX(-50%);
    font-size: 0.7rem;
    color: #fff;
    font-weight: normal;
    white-space: nowrap;
  }
`;


const DisabledGameButton2 = styled(GameButton)`
  position: relative;
  margin-top: 20px;
  
  &::before {
    content: '8시 오픈';
    position: absolute;
    top: -20px;
    left: 50%;
    transform: translateX(-50%);
    font-size: 0.7rem;
    color: #fff;
    font-weight: normal;
    white-space: nowrap;
  }
`;

const DisabledGameButton3 = styled(GameButton)`
  position: relative;
  margin-top: 20px;
  
  &::before {
    content: '10분 후 오픈 예정';
    position: absolute;
    top: -20px;
    left: 50%;
    transform: translateX(-50%);
    font-size: 0.7rem;
    color: #fff;
    font-weight: normal;
    white-space: nowrap;
  }
`;

const RankingButton = styled.button`
  padding: 10px 15px;
  border: 2px solid rgba(255, 255, 255, 0.3);
  border-radius: 12px;
  font-size: 0.9rem;
  font-weight: bold;
  cursor: pointer;
  transition: all 0.3s ease;
  background: rgba(255, 255, 255, 0.1);
  color: white;

  &:hover {
    background: rgba(255, 255, 255, 0.2);
    transform: translateY(-2px);
  }
`;

const UserInfo = styled.div`
  position: absolute;
  top: 20px;
  left: 20px;
  color: white;
  font-size: 1rem;
  text-align: left;

  @media (max-width: 768px) {
    top: 10px;
    left: 10px;
    font-size: 0.8rem;
  }
`;

const BottomSpacer = styled.div`
  width: 100%;
  height: 100px;
  @media (max-width: 768px) {
    height: 100px;
  }
`;

const GameInfoText = styled.p`
  font-size: 0.8rem;
  font-weight: bold;
  margin-bottom: 0;
  margin-top: 50px;
  color: #ffffff;
  text-align: center;
  line-height: 1.5;
`;




function App() {
  const [gameState, setGameState] = useState(INITIAL_STATE);
  const [videoEnded, setVideoEnded] = useState(false);
  const [gameResult, setGameResult] = useState({});
  const [currentGameRound, setCurrentGameRound] = useState(0);
  const [gameResults, setGameResults] = useState([]);
  // userInfo 상태 제거 - currentUser와 userProfile로 통일
  const [serverTime, setServerTime] = useState(new Date()); // 즉시 현재 시간으로 시작
  const [isLoading, setIsLoading] = useState(false); // 로딩 상태 제거
  const [isTestMode, setIsTestMode] = useState(false); // 개발자 테스트 모드
  const [currentQuizIndex, setCurrentQuizIndex] = useState(0); // 현재 퀴즈 인덱스
  const [userInfo, setUserInfo] = useState(null); // 사용자 정보
  const [currentGameType, setCurrentGameType] = useState(GAME_STATES.HOME);
  const [showInfoPopup, setShowInfoPopup] = useState(false);
  const [hasAlreadyWonRoulette, setHasAlreadyWonRoulette] = useState(false);
  const [rhythmConfig, setRhythmConfig] = useState(RHYTHM_CONFIG);
  const [rhythmMedia, setRhythmMedia] = useState([]); // 리듬게임 설정 상태
  const [videoId, setVideoId] = useState('DpTsZjwBjcs'); // 기본 VideoId
  const [isVideoLoading, setIsVideoLoading] = useState(true); // 비디오 로딩 상태

  // 게임 상태를 기반으로 게임 타입 결정 (랭킹 상태일 때는 currentGameType 사용)
  const getCurrentGameType = (state) => {
    if (state === GAME_STATES.RANKING) {
      return currentGameType; // 랭킹 상태일 때는 현재 게임 타입 사용
    }
    return getGameTypeFromState(state);
  };
  const currentTimeRef = useRef(() => 0);
  const videoForcePlayRef = useRef(null);
  const rhythmGameRef = useRef(null);
  const audioControllerRef = useRef(new AudioController());
  const dinoAudioControllerRef = useRef(new AudioController());
  const [quizAnswers, setQuizAnswers] = useState({});




  // Firestore에서 VideoId 가져오기
  useEffect(() => {
    const loadVideoId = async () => {
      try {
        const videoId = await getMainVideoId();
        setVideoId(videoId);
      } catch (error) {
        //console.error('VideoId 로드 실패:', error);
      }
    };
    loadVideoId();
  }, []);

  // VideoId가 변경될 때 로딩 상태 리셋
  useEffect(() => {
    if (videoId) {
      setIsVideoLoading(true);
    }
  }, [videoId]);

  // 비디오 로딩 핸들러
  const handleVideoLoad = () => {
    setIsVideoLoading(false);
  };

  const handleVideoError = () => {
    setIsVideoLoading(false);
    //console.error('비디오 로드 실패');
  };

  // Firestore에서 리듬게임 미디어 가져오기
  useEffect(() => {
    const loadRhythmMedia = async () => {
      try {
        const media = await getRhythmMedia();
        if (media.length > 0) {
          setRhythmMedia(media);
        }
      } catch (error) {
        //console.error('리듬게임 미디어 로드 실패:', error);
      }
    };
    loadRhythmMedia();
  }, []);

  // 개발자 도구에서 사용할 수 있도록 함수들을 window 객체에 추가
  useEffect(() => {
    window.rhythmConfigUtils = {
      getRhythmMedia,
      setRhythmMedia: async () => {
        try {
          const media = await getRhythmMedia();
          setRhythmMedia(media);
        } catch (error) {
          console.error('리듬게임 미디어 업데이트 실패:', error);
        }
      },
      // 비디오 ID 강제 새로고침 함수 추가
      refreshVideoId: async () => {
        try {
          const videoId = await getMainVideoId();
          setVideoId(videoId);
          return videoId;
        } catch (error) {
          console.error('VideoId 새로고침 실패:', error);
          return null;
        }
      }
    };
  }, []);

  // 로컬스토리지에서 기존 게임 결과 불러오기
  useEffect(() => {
    try {
      const savedGameResults = localStorage.getItem('gameResults');
      if (savedGameResults) {
        const parsedResults = JSON.parse(savedGameResults);
        setGameResults(parsedResults);
        //console.log('로컬스토리지에서 게임 결과를 불러왔습니다:', parsedResults);
      }
    } catch (error) {
      //console.error('로컬스토리지에서 게임 결과 불러오기 실패:', error);
    }
  }, []);

  useEffect(() => {
    const updateViewportHeight = () => {
      const height = window.visualViewport ? window.visualViewport.height : window.innerHeight;
      const vh = height * 0.01;
      document.documentElement.style.setProperty('--vh', `${vh}px`);
    };

    updateViewportHeight();

    if (window.visualViewport) {
      window.visualViewport.addEventListener('resize', updateViewportHeight);
    }
    window.addEventListener('resize', updateViewportHeight);
    window.addEventListener('orientationchange', updateViewportHeight);

    return () => {
      if (window.visualViewport) {
        window.visualViewport.removeEventListener('resize', updateViewportHeight);
      }
      window.removeEventListener('resize', updateViewportHeight);
      window.removeEventListener('orientationchange', updateViewportHeight);
    };
  }, []);

  
  // iOS 크롬 감지 함수
  const isChromeOnIOS = () => {
    return /iPhone|iPad|iPod/i.test(navigator.userAgent) && /CriOS/i.test(navigator.userAgent);
  };

  // 카운트다운 음악 재생 핸들러
  const handlePlayTouch = async () => {
    const audio = audioControllerRef.current;
    
    try {
      // 매번 새로운 음악으로 초기화 (모드별로 다른 음악을 위해)
      const currentMedia = rhythmMedia[currentGameRound] || rhythmMedia[0];
      const musicPath = rhythmConfig.RHYTHM_MUSIC[currentMedia.modeKey] || rhythmConfig.DEFAULT_MUSIC;
      
      // 기존 오디오가 있으면 정지
      if (audio.audioElement) {
        try {
          await audio.stop();
        } catch (stopErr) {
          console.warn('기존 오디오 정지 중 오류:', stopErr);
        }
      }
      
      // 새로운 음악으로 초기화
      await audio.init(musicPath);
      
      // 모바일에서 더 확실한 unlock 수행
      await audio.unlock();
      
      // 모바일에서 추가 대기 시간 (iOS 크롬에서는 더 길게)
      if (/iPhone|iPad|iPod|Android/i.test(navigator.userAgent)) {
        const waitTime = isChromeOnIOS() ? 500 : 300;
        await new Promise(resolve => setTimeout(resolve, waitTime));
      }
      
    } catch (error) {
      // 무해한 에러는 무시
      if (error && error.target && error.target.error === null) {
        //console.log('Audio initialization completed (ignoring harmless error)');
        return;
      }
      //console.error('Audio initialization error:', error);
      //  console.error('Error details:', {
      //   name: error.name,
      //   message: error.message,
      //   stack: error.stack
      // });
    }
  };

  /// 카운트다운 음악 재생 핸들러
  const handleCountdownEnd = async () => {
    const audio = audioControllerRef.current;
    
    try {
      // 오디오가 이미 초기화되어 있는지 확인 (handlePlayTouch에서 초기화됨)
      if (!audio.audioElement) {
        //console.error('오디오가 초기화되지 않았습니다. handlePlayTouch를 먼저 실행해주세요.');
        setGameState(GAME_STATES.HOME);
        return;
      }
      
      // iOS에서 더 안정적인 재생을 위한 추가 처리
      if (/iPhone|iPad|iPod/i.test(navigator.userAgent)) {
        // iOS에서는 재생 전에 약간의 지연 (크롬에서는 더 길게)
        const waitTime = isChromeOnIOS() ? 200 : 100;
        await new Promise(resolve => setTimeout(resolve, waitTime));
      }
      
      // (1) 카운트다운 끝나면 바로 음악 재생
      await audio.play();
      
      // (2) currentTimeRef 설정
      currentTimeRef.current = () => audio.getCurrentTime();
      
      // (3) 음악 재생이 성공하면 곧바로 게임 화면으로 전환
      setGameState(GAME_STATES.RHYTHM_GAME);
    } catch (err) {
      //console.error('카운트다운 후 오디오 재생 실패:', err);
      
      // iOS에서 재생 실패 시 재시도 (크롬에서는 더 긴 대기)
      if (/iPhone|iPad|iPod/i.test(navigator.userAgent)) {
        try {
          //console.log('iOS에서 오디오 재생 재시도 중...');
          const retryWaitTime = isChromeOnIOS() ? 1000 : 500;
          await new Promise(resolve => setTimeout(resolve, retryWaitTime));
          
          // 안전한 재시도 메서드 사용 (MediaElementSource 연결 오류 방지)
          await audio.safeRetry();
          currentTimeRef.current = () => audio.getCurrentTime();
          setGameState(GAME_STATES.RHYTHM_GAME);
          return;
        } catch (retryErr) {
          //console.warn('Audio retry failed:', retryErr);
        }
      }
      
      // 음성 재생에 실패하면 오디오 정지 후 홈으로 이동
      //console.error('오디오 재생 실패로 홈으로 이동합니다.');
      try {
        await audio.stop();
      } catch (stopErr) {
        //console.warn('오디오 정지 중 오류:', stopErr);
      }
      setGameState(GAME_STATES.HOME);
    }
  };


  const handleVideoEnd = () => {
    setVideoEnded(true);
    setGameState(GAME_STATES.TOUCH);
  };


  // ----------------------------------------
  // 리듬게임 핸들러
  // ----------------------------------------
  /// 리듬게임 난이도별 영상 정보
  const currentMedia = rhythmMedia[currentGameRound] || rhythmMedia[0];
  /// 게임하기 버튼 클릭 시 초기화 및 게임 시작
  const handleStartGame = async () => {
    // 기존 오디오 정지
    try {
      const audio = audioControllerRef.current;
      if (audio && audio.isPlaying) {
        await audio.stop();
      }
    } catch (stopErr) {
      //console.warn('게임 시작 시 오디오 정지 중 오류:', stopErr);
    }
    
    setCurrentGameType(GAME_STATES.RHYTHM_GAME);
    setCurrentGameRound(0);
    setGameResults([]);
    // 로컬스토리지 초기화
    try {
      localStorage.removeItem('gameResults');
      //console.log('로컬스토리지 게임 결과를 초기화했습니다.');
    } catch (error) {
      //console.error('로컬스토리지 초기화 실패:', error);
    }
    setGameState(GAME_STATES.VIDEO); // VIDEO
    setVideoEnded(false);
  };

  const handleLevelUp = async () => {
    // 기존 오디오 정지
    try {
      const audio = audioControllerRef.current;
      if (audio && audio.isPlaying) {
        await audio.stop();
      }
    } catch (stopErr) {
      //console.warn('레벨업 시 오디오 정지 중 오류:', stopErr);
    }
    
    setCurrentGameRound(currentGameRound + 1);
    setGameState(GAME_STATES.VIDEO); // VIDEO
    setVideoEnded(true);
  };

  const handleGameEnd = (result) => {
    const currentMedia = rhythmMedia[currentGameRound] || rhythmMedia[0];
    const roundResult = {
      ...result,
      round: currentGameRound + 1,
      modeKey: currentMedia.modeKey,
      modeDisplayName: currentMedia.modeDisplayName,
      maxScore: currentMedia.maxScore || (currentMedia.modeKey === 'hard' ? 4000 : 3000)
    };

    // 기존 코드 주석처리 (라운드 결과 저장 방식 변경)
    /*
    if (currentGameRound < 2) {
      setGameResult(roundResult);
      setGameState(GAME_STATES.RHYTHM_RESULT);
    } else {
      const totalScore = newGameResults.reduce((sum, result) => sum + result.score, 0);
      const totalCombo = newGameResults.reduce((sum, result) => sum + result.combo, 0);
      const maxTotalScore = GAME_MEDIA.reduce((sum, m) => sum + (m.maxScore || (m.modeKey === 'hard' ? 4000 : 3000)), 0);

      const finalResult = {
        totalScore,
        totalCombo,
        maxTotalScore,
        roundResults: newGameResults,
        timestamp: toKstIso(Timestamp.now())
      };

      setGameResult(finalResult);
      setGameState(GAME_STATES.RHYTHM_ENTRY);
    }
    */

    // 현재 메모리의 gameResults에 새 결과 추가
    const newGameResults = [...gameResults, roundResult];
    setGameResults(newGameResults);
    // 로컬스토리지에 저장 (현재 게임의 모든 라운드 결과)
    try {
      localStorage.setItem('gameResults', JSON.stringify(newGameResults));
      //console.log('게임 결과를 로컬스토리지에 저장했습니다:', newGameResults);
    } catch (error) {
      //console.error('로컬스토리지 저장 실패:', error);
    }

    if (currentGameRound < 2) {
      setGameResult(roundResult);
      setGameState(GAME_STATES.RHYTHM_RESULT);
    } else {
      // 마지막 라운드: 로컬스토리지에서 모든 라운드 결과를 가져와서 Firebase에 저장
      try {
        const savedResults = localStorage.getItem('gameResults');
        if (savedResults) {
          const allRoundResults = JSON.parse(savedResults);
          //console.log('마지막 라운드: 로컬스토리지에서 모든 라운드 결과를 가져왔습니다:', allRoundResults);
          
          const totalScore = allRoundResults.reduce((sum, result) => sum + result.score, 0);
          const totalCombo = allRoundResults.reduce((sum, result) => sum + result.combo, 0);
          const maxTotalScore = rhythmMedia.reduce((sum, m) => sum + (m.maxScore || (m.modeKey === 'hard' ? 4000 : 3000)), 0);

          const finalResult = {
            totalScore,
            totalCombo,
            maxTotalScore,
            roundResults: allRoundResults, // 로컬스토리지에 저장된 모든 라운드 결과
            timestamp: toKstIso(Timestamp.now())
          };

          setGameResult(finalResult);
          setGameState(GAME_STATES.RHYTHM_ENTRY);
        } else {
          //console.error('로컬스토리지에서 게임 결과를 찾을 수 없습니다.');
        }
      } catch (error) {
        //console.error('마지막 라운드 결과 처리 실패:', error);
      }
    }
  };

  // ----------------------------------------
  // 랭킹 핸들러
  // 메인 화면에서 랭킹보기로 이동시 기본은 퍼즐랭킹
  // ----------------------------------------
  const handleShowRanking = () => {
    setGameState(GAME_STATES.RANKING);
    setCurrentGameType(GAME_STATES.PUZZLE_GAME);
  };

  // ----------------------------------------
  // 홈으로 이동 핸들러
  // ----------------------------------------
  const handleBackToHome = async () => {
    // 홈으로 돌아갈 때 오디오 정지
    try {
      const audio = audioControllerRef.current;
      if (audio && audio.isPlaying) {
        await audio.stop();
      }
    } catch (stopErr) {
      //console.warn('홈으로 돌아갈 때 오디오 정지 중 오류:', stopErr);
    }
    setGameState(GAME_STATES.HOME);
  };

  // ----------------------------------------
  // 게임 재시작 핸들러
  // ----------------------------------------
  const handleRetryGame = async (gameType) => {
    // 전달받은 게임 타입에 따라 다른 재시작 로직
    switch (gameType) {
      case GAME_STATES.RHYTHM_GAME:
        // 기존 오디오 정지
        try {
          const audio = audioControllerRef.current;
          if (audio && audio.isPlaying) {
            await audio.stop();
          }
        } catch (stopErr) {
          //console.warn('리듬게임 재시작 시 오디오 정지 중 오류:', stopErr);
        }
        
        setCurrentGameType(GAME_STATES.RHYTHM_GAME);
        setCurrentGameRound(0);
        setGameResults([]);
        // 로컬스토리지 초기화
        try {
          localStorage.removeItem('gameResults');
          //console.log('로컬스토리지 게임 결과를 초기화했습니다.');
        } catch (error) {
          //console.error('로컬스토리지 초기화 실패:', error);
        }
        setGameState(GAME_STATES.VIDEO); // VIDEO
        setVideoEnded(false);
        break;
      case GAME_STATES.PUZZLE_GAME:
        // 4x4를 풀고 응모하기에서 다시시도할 때만 4x4로 시작
        if (gameState === GAME_STATES.PUZZLE_RESULT && gameResult && gameResult.stage === 2) {
          // 4x4 퍼즐 완성 후 응모 화면에서 다시시도
          handleStart4x4Puzzle();
        } else {
          // 그 외 모든 경우: 3x3 퍼즐부터 시작
          setCurrentGameType(GAME_STATES.PUZZLE_GAME);
          setGameState(GAME_SETTINGS.puzzle.gameState);
        }
        break;
      case GAME_STATES.QUIZ_GAME:
        setCurrentGameType(GAME_STATES.QUIZ_GAME);
        setCurrentQuizIndex(0);
        setQuizAnswers({});
        setGameState(GAME_SETTINGS.quiz.gameState);
        break;
      case GAME_STATES.WORLDCUP_GAME:
        setCurrentGameType(GAME_STATES.WORLDCUP_GAME);
        setGameState(GAME_SETTINGS.worldcup.gameState);
        break;
      case GAME_STATES.DINO_GAME:
        setCurrentGameType(GAME_STATES.DINO_GAME);
        setGameState(GAME_SETTINGS.dino.gameState);
        break;
      case GAME_STATES.DRIP_BATTLE:
        setCurrentGameType(GAME_STATES.DRIP_BATTLE);
        setGameState(GAME_SETTINGS.dripBattle.gameState);
        break;
      default:
        setGameState(GAME_STATES.HOME);
    }
  };


  // ----------------------------------------
  /// 게임 결과 제출 후 룰렛으로 이동
  // ----------------------------------------
  const handleGameResultSubmit = async (submissionData) => {
    // 게임 결과 제출 후 룰렛 당첨 여부 확인
    const gameType = getCurrentGameType(gameState);
    
    // 응모한 사용자 정보를 userInfo에 저장
    setUserInfo({
      nickname: submissionData.nickname,
      phone: submissionData.phone
    });
    
    try {
      // 사용자가 이미 룰렛에서 당첨했는지 확인 (전화번호로 확인)
      const hasWon = await hasUserWonRoulette(submissionData.phone);
      setHasAlreadyWonRoulette(hasWon);
      
      if (hasWon) {
        // 이미 당첨된 경우 게임 타입에 따라 다른 화면으로 이동
        if(currentGameType === GAME_STATES.RHYTHM_GAME || 
          currentGameType === GAME_STATES.DINO_GAME ||
          currentGameType === GAME_STATES.WORLDCUP_GAME) {
          setGameState(GAME_STATES.RANKING);
        } else if (currentGameType === GAME_STATES.PUZZLE_GAME && gameResult && gameResult.stage === 1) {
          // 퍼즐 3x3인 경우에만 룰렛 화면으로 이동 (4x4 도전 버튼 표시)
          setGameState(GAME_STATES.ROULETTE);
        } else if (currentGameType === GAME_STATES.PUZZLE_GAME && gameResult && gameResult.stage === 2) {
          // 퍼즐 4x4인 경우 랭킹으로 이동
          setGameState(GAME_STATES.RANKING);
        } else if (currentGameType === GAME_STATES.DRIP_BATTLE) {
          // 드립배틀 완료 후 룰렛으로 이동
          setGameState(GAME_STATES.ROULETTE);
        } else {
          setGameState(GAME_STATES.HOME);
        }
      } else {
        // 당첨되지 않은 경우 룰렛으로 이동
        setGameState(GAME_STATES.ROULETTE);
      }
    } catch (error) {
      //console.error('룰렛 당첨 여부 확인 중 오류:', error);
      // 오류 발생 시 기본적으로 룰렛으로 이동
      setGameState(GAME_STATES.ROULETTE);
    }
  };

  // ----------------------------------------
  /// 룰렛 게임 완료 후 홈으로 이동
  // ----------------------------------------
  const handleRouletteComplete = () => {
    // 퍼즐게임의 경우 3x3 완료 후 룰렛에서 당첨되면 홈으로, 4x4 완료 후 룰렛에서 당첨되면 랭킹으로
    if (currentGameType === GAME_STATES.PUZZLE_GAME) {
      if (gameResult && gameResult.stage === 2) {
        // 4x4 퍼즐 완료 후 룰렛에서 당첨되면 랭킹으로 이동
        setGameState(GAME_STATES.RANKING);
      } else {
        // 3x3 퍼즐 완료 후 룰렛에서 당첨되면 홈으로 이동
        setGameState(GAME_STATES.HOME);
      }
    } else if (
      currentGameType === GAME_STATES.RHYTHM_GAME ||
      currentGameType === GAME_STATES.DINO_GAME ||
      currentGameType === GAME_STATES.WORLDCUP_GAME
    ) {
      setGameState(GAME_STATES.RANKING);
    } else {
      setGameState(GAME_STATES.HOME);
    }
  };

  const handleEntryBack = () => {
    setGameState(GAME_STATES.HOME);
  };
  
  // ----------------------------------------
  // 퍼즐게임 핸들러
  // ----------------------------------------
  const handlePuzzleGame = () => {
    setCurrentGameType(GAME_STATES.PUZZLE_GAME);
    setGameState(GAME_SETTINGS.puzzle.gameState);
    // 메인에서 퍼즐 시작 시 항상 3x3 모드로 설정
    setGameResult({ is4x4Mode: false });
  };
  
  const handlePuzzleComplete = (puzzleResult) => {
    // 퍼즐 결과에 stage 정보가 포함되어 있음
    // 3x3과 4x4 모두 응모 화면으로 이동
    setGameResult(puzzleResult);
    setGameState(GAME_SETTINGS.puzzle.resultState);
  };

  // 4x4 퍼즐 시작 핸들러 추가
  const handleStart4x4Puzzle = () => {
    setCurrentGameType(GAME_STATES.PUZZLE_GAME);
    setGameState(GAME_SETTINGS.puzzle.gameState);
    // 4x4 모드 플래그 설정
    setGameResult({ ...gameResult, is4x4Mode: true });
  };

  // ----------------------------------------
  // 월드컵 핸들러
  // ----------------------------------------
  const handleWorldCup = () => {
    setCurrentGameType(GAME_STATES.WORLDCUP_GAME);
    setGameState(GAME_SETTINGS.worldcup.gameState);
  };

  const handleWorldCupComplete = (worldCupResult) => {
    // 월드컵 게임 완료 후 결과 화면으로 이동
    setGameResult(worldCupResult);
    setGameState(GAME_SETTINGS.worldcup.resultState);
  };

  // ----------------------------------------
  // 퀴즈 게임 핸들러
  // ----------------------------------------
  const handleQuizGame = () => {
    setCurrentGameType(GAME_STATES.QUIZ_GAME);
    setCurrentQuizIndex(0); // 첫 번째 퀴즈부터 시작
    setGameState(GAME_SETTINGS.quiz.gameState);
  };

  const handleQuizComplete = (selectedAnswer) => {
    // 현재 라운드 답변 저장
    setQuizAnswers(prev => ({
      ...prev,
      [`round${currentQuizIndex + 1}Answer`]: selectedAnswer
    }));
    
    // 다음 퀴즈로 이동하거나 완료
    if (currentQuizIndex < 2) { // 3개 퀴즈 (0, 1, 2)
      setCurrentQuizIndex(currentQuizIndex + 1);
    } else {
      // 모든 퀴즈 완료 - 결과 화면으로 이동
      const quizResult = {
        round1Answer: quizAnswers.round1Answer,
        round2Answer: quizAnswers.round2Answer,
        round3Answer: selectedAnswer
      };
      setGameResult(quizResult);
      setGameState(GAME_SETTINGS.quiz.resultState);
    }
  };

  // ----------------------------------------
  // 드립배틀 핸들러
  // ----------------------------------------
  const handleDripBattle = () => {
    setCurrentGameType(GAME_STATES.DRIP_BATTLE);
    setGameState(GAME_SETTINGS.dripBattle.gameState);
  };

  const handleDripBattleComplete = (dripBattleResult) => {
    // 드립배틀 완료 - 결과 화면으로 이동
    setGameResult(dripBattleResult);
    setGameState(GAME_SETTINGS.dripBattle.resultState);
  };

  const handleStartDripBattle = () => {
    // 드립배틀 재시작
    setGameState(GAME_STATES.DRIP_BATTLE);
  };

  // ----------------------------------------
  // 공룡게임 핸들러
  // ----------------------------------------
  const handleDinoGame = async () => {
    setGameState(GAME_STATES.DINO_GAME);
    setCurrentGameType(GAME_STATES.DINO_GAME);
  };

  const handleDinoComplete = (dinoResult) => {
    //console.log('공룡게임 완료:', dinoResult);
    setGameResult(dinoResult);
    setGameState(GAME_SETTINGS.dino.resultState);
  };
  
  // ----------------------------------------
  // InfoPopup 관련 핸들러
  // ----------------------------------------
  const handleShowInfoPopup = () => {
    setShowInfoPopup(true);
  };

  const handleCloseInfoPopup = () => {
    setShowInfoPopup(false);
  };

  // InfoPopup 자동 표시 로직 (오늘 하루 보지 않기 체크 시 숨김)
  useEffect(() => {
    const today = new Date();
    const yyyy = today.getFullYear();
    const mm = String(today.getMonth() + 1).padStart(2, '0');
    const dd = String(today.getDate()).padStart(2, '0');
    const dateStr = `${yyyy}-${mm}-${dd}`;
    
    const hideDate = localStorage.getItem('prize_info_hide_date');
    if (hideDate !== dateStr) {
      // 오늘 하루 보지 않기로 설정되지 않았다면 팝업 표시
      setShowInfoPopup(true);
    }
  }, []);


  
  // 개발자 테스트 모드가 아니고, 시간이12:45전이면 준비중 화면 표시
  // // if (!isTestMode && kstNow < kstNoon) {
  if (!isTestMode) {
    return <ComingSoon onStartGame={() => {
      //setIsTestMode(true); // 테스트 모드 활성화
      //setGameState(GAME_STATES.HOME); // home, video, touch, rhythm_game, rhythm_result, ranking, rhythm_entry, roulette
    }} />;

    /// 파이어스토어 스트레스 테스트
    // //return <StressTest />;
  }

  return (
    <AppLayout>
      {/* <BackgroundImage> */}
      <Banner>
        {gameState !== GAME_STATES.HOME && (
          <BackButton onClick={handleBackToHome}>
            <FontAwesomeIcon icon={faHome} size="lg" />
          </BackButton>
        )}
        <img src="/YOUNGPOSSE_Logo.png?v=2" alt="YOUNG POSSE" />
      </Banner>
      {/* 룰렛 당첨자 알림 배너 */}
      <RouletteBanner />

      <MainContent>
        {gameState === GAME_STATES.TOUCH && (
            <Countdown onCountdownEnd={handleCountdownEnd} onPlayTouch={handlePlayTouch} round={currentGameRound + 1} />
          )}
        {(gameState === GAME_STATES.VIDEO) && (
          /// 유튜브 Iframe 버전
          <VideoPlayerIframe
            videoUrl={currentMedia.videoUrl}
            onVideoEnd={handleVideoEnd}
          />
        )}
        {/* Info 팝업을 항상 최상단에 렌더링 */}
        {showInfoPopup && (
          <InfoPopup onClose={handleCloseInfoPopup} />
        )}

        {/* 홈 화면 */}
        {gameState === GAME_STATES.HOME && (
          <HomeScreen
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ 
              duration: 0.6,
              ease: "easeOut"
            }}
          >           
              <VideoContainer>
               {isVideoLoading && <VideoLoadingSpinner />}
               {videoId && (
                 <iframe
                   style={{
                     width: "100%",
                     height: "auto",
                     borderRadius: "15px",
                     aspectRatio: "16/9",
                     opacity: isVideoLoading ? 0 : 1,
                     transition: "opacity 0.3s ease"
                   }}
                   src={`https://www.youtube.com/embed/${videoId}?autoplay=1&controls=0&rel=0&mute=1&loop=1&playlist=${videoId}`}
                   title="YOUNG POSSE"
                   frameBorder="0"
                   allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; controls"
                   allowFullScreen
                   onLoad={handleVideoLoad}
                   onError={handleVideoError}
                 ></iframe>
               )}
             </VideoContainer>
            
            <ButtonGridContainer>
              <ButtonGrid
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ 
                  duration: 0.6,
                  delay: 0.1,
                  ease: [0.25, 0.46, 0.45, 0.94]
                }}
              >
              <GameButton 
                 disabled={false}
                 onClick={handleStartGame}
                 initial={{ opacity: 0, y: 8 }}
                 animate={{ opacity: 1, y: 0 }}
                 transition={{ 
                   duration: 0.3, 
                   delay: 0.1,
                   ease: [0.25, 0.46, 0.45, 0.94],
                   hover: { duration: 0 },
                   tap: { duration: 0 }
                 }}
                 whileHover={{ 
                  scale: 1.10,
                  boxShadow: "0 10px 30px rgba(255, 107, 107, 0.8)"
                }}
                 whileTap={{ 
                   scale: 1.0,
                   boxShadow: "0 4px 15px rgba(102, 102, 102, 0.3)"
                 }}
               >
                 🎵 리듬게임
               </GameButton>
               <GameButton 
                  disabled={false}
                  onClick={handleDinoGame}
                  initial={{ opacity: 0, y: 8 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ 
                    duration: 0.3, 
                    delay: 0.1,
                    ease: [0.25, 0.46, 0.45, 0.94],
                    hover: { duration: 0 },
                    tap: { duration: 0 }
                  }}
                  whileHover={{ 
                    scale: 1.10,
                    boxShadow: "0 10px 30px rgba(255, 107, 107, 0.8)"
                  }}
                  whileTap={{ 
                    scale: 1.0,
                    boxShadow: "0 4px 15px rgba(102, 102, 102, 0.3)"
                  }}
                >
                  🛹 파씨런
                </GameButton>
               <GameButton 
                    onClick={handlePuzzleGame}
                    initial={{ opacity: 0, y: 8 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ 
                      duration: 0.3, 
                      delay: 0.1,
                      ease: [0.25, 0.46, 0.45, 0.94],
                      hover: { duration: 0 },
                      tap: { duration: 0 }
                    }}
                    whileHover={{ 
                      scale: 1.1,
                      boxShadow: "0 10px 30px rgba(255, 107, 107, 0.8)"
                    }}
                    whileTap={{ 
                      scale: 0.92,
                      boxShadow: "0 2px 10px rgba(255, 107, 107, 0.4)"
                    }}
                  >
                 🧩 퍼즐
               </GameButton>
               {/* <GameButton 
                 onClick={handleQuizGame}
                 initial={{ opacity: 0, y: 8 }}
                 animate={{ opacity: 1, y: 0 }}
                 transition={{ 
                   duration: 0.3, 
                   delay: 0.1,
                   ease: [0.25, 0.46, 0.45, 0.94],
                   hover: { duration: 0 },
                   tap: { duration: 0 }
                 }}
                 whileHover={{ 
                   scale: 1.1,
                   boxShadow: "0 10px 30px rgba(255, 107, 107, 0.8)"
                 }}
                 whileTap={{ 
                   scale: 0.92,
                   boxShadow: "0 2px 10px rgba(255, 107, 107, 0.4)"
                 }}
               >
                 🎤 퀴즈 퀴즈
               </GameButton> */}
                <GameButton
                   onClick={handleDripBattle}
                   initial={{ opacity: 0, y: 8 }}
                   animate={{ opacity: 1, y: 0 }}
                   transition={{ 
                     duration: 0.3, 
                     delay: 0.1,
                     ease: [0.25, 0.46, 0.45, 0.94],
                     hover: { duration: 0 },
                     tap: { duration: 0 }
                    }}
                   whileHover={{ 
                     scale: 1.08,
                     boxShadow: "0 10px 30px rgba(255, 107, 107, 0.8)"
                   }}
                   whileTap={{ 
                     scale: 0.92,
                     boxShadow: "0 2px 10px rgba(255, 107, 107, 0.4)"
                   }}
                  >
                   💬 드립배틀
                </GameButton>
                <GameButton 
                  onClick={handleWorldCup}
                  initial={{ opacity: 0, y: 8 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ 
                    duration: 0.3, 
                    delay: 0.1,
                    ease: [0.25, 0.46, 0.45, 0.94],
                    hover: { duration: 0 },
                    tap: { duration: 0 }
                  }}
                  whileHover={{ 
                    scale: 1.10,
                    boxShadow: "0 10px 30px rgba(255, 107, 107, 0.8)"
                  }}
                  whileTap={{ 
                    scale: 0.92,
                    boxShadow: "0 2px 10px rgba(255, 107, 107, 0.4)"
                  }}
                >
                 📱 숏츠월드컵
              </GameButton>
              <RankingButton onClick={handleShowRanking}>🏆 랭킹 보기</RankingButton>
              </ButtonGrid>
            </ButtonGridContainer>
          </HomeScreen>
        )}

        {/* 리듬 게임 */}
        {gameState === GAME_STATES.RHYTHM_GAME && (
          <RhythmGame
            ref={rhythmGameRef}
            onGameEnd={handleGameEnd}
            currentTimeRef={currentTimeRef}
            gameMode={currentMedia.modeKey}
            currentRound={currentGameRound + 1}
            totalRounds={rhythmMedia.length}
            music={rhythmConfig.RHYTHM_MUSIC[currentMedia.modeKey]}
            beatmap={BEATMAPS[currentMedia.modeKey]}
            audioController={audioControllerRef.current}
            rhythmConfig={rhythmConfig}
          />
        )}
        {gameState === GAME_STATES.RHYTHM_RESULT && (
          <RoundResult
            currentRound={currentGameRound + 1}
            result={gameResult}
            onLevelUp={handleLevelUp}
          />
        )}
        {/* 리듬 게임 응모 화면 */}
        {/* {gameState === GAME_STATES.RHYTHM_ENTRY && (
          <EntryForm
            gameResult={gameResult}
            onSubmit={handleEntrySubmit}
            onRetry={handleRetryGame}
          />
        )} */}
        
        {/* 퍼즐 게임 */}
        {gameState === GAME_STATES.PUZZLE_GAME && (
          <PuzzleGame 
            onBackToHome={handleBackToHome} 
            onComplete={handlePuzzleComplete}
            is4x4Mode={gameResult?.is4x4Mode || false}
          />
        )}

        {/* 월드컵 게임 */}
        {gameState === GAME_STATES.WORLDCUP_GAME && (
          <WorldCup onBackToHome={handleBackToHome} onComplete={handleWorldCupComplete} />
        )}

        {/* 퀴즈 게임 */}
        {gameState === GAME_STATES.QUIZ_GAME && (
          <QuizGame 
            onComplete={handleQuizComplete}
            isFirst={currentQuizIndex === 0}
            isSecond={currentQuizIndex === 1}
            isThird={currentQuizIndex === 2}
          />
        )}

        {/* 드립배틀 게임 */}
        {gameState === GAME_STATES.DRIP_BATTLE && (
          <DripBattle 
            onComplete={handleDripBattleComplete}
          />
        )}

        {/* 공룡게임 */}
        {gameState === GAME_STATES.DINO_GAME && (
          (() => {
            return (
              <DinoGame 
                onBackToHome={handleBackToHome} 
                onComplete={handleDinoComplete} 
                audioController={dinoAudioControllerRef.current}
              />
            );
          })()
        )}

        {/* 게임 결과 화면 */}
        {(gameState === GAME_STATES.PUZZLE_RESULT 
            || gameState === GAME_STATES.QUIZ_RESULT 
            || gameState === GAME_STATES.WORLDCUP_RESULT 
            || gameState === GAME_STATES.RHYTHM_ENTRY
            || gameState === GAME_STATES.DINO_RESULT
            || gameState === GAME_STATES.DRIP_BATTLE_RESULT
        ) && (
          <GameResultForm
            gameResult={gameResult}
            onSubmit={handleGameResultSubmit}
            onRetry={() => {
              const gameType = getCurrentGameType(gameState);
              handleRetryGame(gameType);
            }}
            gameType={getCurrentGameType(gameState)}
            userInfo={userInfo}
          />
        )}

        {/* 룰렛 게임 결과 화면 */}
        {gameState === GAME_STATES.ROULETTE && (
          <RouletteEvent 
            userInfo={userInfo} 
            onComplete={handleRouletteComplete}
            gameType={currentGameType}
            onStart4x4Puzzle={handleStart4x4Puzzle}
            onStartDripBattle={handleStartDripBattle}
            gameResult={gameResult}
            hasAlreadyWon={hasAlreadyWonRoulette}
          />
        )}

        {/* 랭킹 화면 */}
        {gameState === GAME_STATES.RANKING && (
          <Ranking 
            onBack={handleBackToHome} 
            onRetry={handleRetryGame} 
            userInfo={userInfo} 
            gameType={getCurrentGameType(gameState)}
          />
        )}

      </MainContent>
      <BottomSpacer />
      {/* </BackgroundImage> */}
    </AppLayout>
  );
}

export default App;
